import { outcomeDefinitionType } from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import type {
  TeammateAgentAutoEnrollment,
  TeammateAgentAutoEnrollmentPlaybook,
} from "@repo/app-types/entities/teammateAgent";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import {
  MultiSelect,
  type MultiSelectSection,
} from "@repo/ui/Select/multiSelect";
import { useId, type FC } from "react";
import { useAllPlaybooksQuery } from "../../../api/entities/playbook.js";
import { PlaybookAndVoiceAgentMultiSelect } from "../../../components/playbookAndVoiceAgentMultiSelect.js";
import type { PlaybookAndVoiceAgentOption } from "../../../hooks/usePlaybookAndVoiceAgentOptions.js";

/**
 * An outcome option's id in the select: the playbook and the value, since a
 * value only means something within its playbook's outcome definition.
 * Playbook ids are UUIDs, so the separator cannot occur in them.
 */
const OUTCOME_OPTION_ID_SEPARATOR = "|";

function buildOutcomeOptionId(playbookId: PlaybookId, value: string): string {
  return `${playbookId}${OUTCOME_OPTION_ID_SEPARATOR}${value}`;
}

function parseOutcomeOptionId(
  id: string,
): { playbookId: string; value: string } | undefined {
  const index = id.indexOf(OUTCOME_OPTION_ID_SEPARATOR);
  return index === -1
    ? undefined
    : { playbookId: id.slice(0, index), value: id.slice(index + 1) };
}

export interface AutoEnrollmentSectionProps {
  autoEnrollment: TeammateAgentAutoEnrollment | undefined;
  isDisabled: boolean;
  onChange: (autoEnrollment: TeammateAgentAutoEnrollment) => void;
}

/**
 * Enrolling contacts from a human teammate's graded calls: which playbooks,
 * and which of each one's outcomes. Only meaningful on an agent-managed
 * agent, whose first turn reads the call and decides what to do and when.
 */
export const AutoEnrollmentSection: FC<AutoEnrollmentSectionProps> = (
  props,
) => {
  const { autoEnrollment, isDisabled, onChange } = props;
  const labelId = useId();
  const descriptionId = useId();
  const {
    data: playbooks,
    isLoading,
    isSuccess,
  } = useAllPlaybooksQuery({ exclude_teammate_agent_playbooks: "true" });

  const enrolledPlaybooks = autoEnrollment?.playbooks ?? [];
  const playbookOptions: PlaybookAndVoiceAgentOption[] = (playbooks ?? []).map(
    (playbook) => ({
      value: playbook.id,
      label: playbook.name,
      kind: "playbook",
    }),
  );

  // One section per chosen playbook, headed by its name, holding that
  // playbook's outcome values. Only a preset outcome is ever extracted from a
  // call (custom definitions are skipped by outcome extraction), so a custom
  // one could never enroll anyone and is not offered.
  const outcomeSections: MultiSelectSection[] = enrolledPlaybooks.flatMap(
    (entry) => {
      const playbook = playbooks?.find(
        (candidate) => candidate.id === entry.playbook_id,
      );
      const definition = playbook
        ? getOutcomeDefinitionFromPlaybook(playbook)
        : undefined;
      const results =
        definition?.type === outcomeDefinitionType.preset
          ? definition.results
          : [];
      return playbook && results.length > 0
        ? [
            {
              id: playbook.id,
              label: playbook.name,
              options: results.map((result) => ({
                id: buildOutcomeOptionId(playbook.id, result.value),
                label: result.label,
              })),
            },
          ]
        : [];
    },
  );
  const selectedOutcomeIds = enrolledPlaybooks.flatMap((entry) =>
    entry.outcome_values.map((value) =>
      buildOutcomeOptionId(entry.playbook_id, value),
    ),
  );
  // What the closed picker shows: the one chosen outcome by name, or a count.
  const selectedOutcomeLabels = outcomeSections.flatMap((section) =>
    section.options
      .filter((option) => selectedOutcomeIds.includes(option.id))
      .map((option) => option.label),
  );
  const outcomeSelectionLabel =
    selectedOutcomeLabels.length === 0
      ? undefined
      : selectedOutcomeLabels.length === 1
        ? selectedOutcomeLabels[0]
        : `${selectedOutcomeLabels.length} selected`;

  const update = (playbooks: TeammateAgentAutoEnrollmentPlaybook[]) => {
    onChange({ playbooks });
  };

  const handlePlaybooksChange = (playbookIds: readonly PlaybookId[]) => {
    // Keep the outcomes already chosen for a playbook that stays selected.
    update(
      playbookIds.map(
        (playbook_id) =>
          enrolledPlaybooks.find(
            (entry) => entry.playbook_id === playbook_id,
          ) ?? { playbook_id, outcome_values: [] },
      ),
    );
  };

  const handleOutcomesChange = (optionIds: readonly string[]) => {
    const selected = optionIds.flatMap((id) => {
      const parsed = parseOutcomeOptionId(id);
      return parsed ? [parsed] : [];
    });
    update(
      enrolledPlaybooks.map((entry) => ({
        ...entry,
        outcome_values: selected
          .filter((option) => option.playbookId === entry.playbook_id)
          .map((option) => option.value),
      })),
    );
  };

  return (
    <div>
      <Label size={Size.SMALL} id={labelId}>
        Enroll from teammate calls
      </Label>
      <FieldDescription id={descriptionId} className="mt-1">
        When a teammate&apos;s call is graded with one of the outcomes below,
        the agent gets a task for that contact with the call on its timeline,
        and decides on its first turn whether and when to reach out.
      </FieldDescription>

      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        className="mt-3 flex max-w-md flex-col gap-4"
      >
        <div>
          <Label size={Size.SMALL}>Playbooks</Label>
          <div className="mt-1">
            <PlaybookAndVoiceAgentMultiSelect
              options={playbookOptions}
              isLoading={isLoading}
              hasLoadedAllOptions={isSuccess}
              selectedPlaybookIds={enrolledPlaybooks.map(
                (entry) => entry.playbook_id,
              )}
              size={Size.SMALL}
              isDisabled={isDisabled}
              onChange={({ playbookIds }) => handlePlaybooksChange(playbookIds)}
            />
          </div>
        </div>

        <div>
          <Label size={Size.SMALL}>Outcomes that enroll</Label>
          <div className="mt-1">
            <MultiSelect
              label={outcomeSelectionLabel}
              placeholder={
                enrolledPlaybooks.length === 0
                  ? "Choose playbooks first"
                  : outcomeSections.length === 0
                    ? "The chosen playbooks have no outcome a call can be graded with"
                    : "Choose outcomes"
              }
              size={Size.SMALL}
              isDisabled={isDisabled || outcomeSections.length === 0}
              sections={outcomeSections}
              value={selectedOutcomeIds}
              onChange={handleOutcomesChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
